/* eslint-disable react/prop-types */
import React, { Children, isValidElement, useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Header } from '@/components/design-system/molecules/header';
import { useAuthContext } from '@/hooks/use-auth';
import { ConnectedHeaderProps, Slots } from './types';
import { COLOC_MAIN_APP } from '@/config';
import { UserMenu } from './components/user-menu';
import { LocaleSwitcher } from './components/locale-selector';
import { ConnectedMobileSideMenu } from './components/connected-mobile-side-menu';
import { theme } from '@/utils/util_theme';
import { IconSaxIcon } from '@/components/design-system/atoms/icon';
import { CitiesGridModal } from '@/features/marketplace/components/cities-grid-modal';
import { CitiesListContextProvider } from '@/features/marketplace/hooks/use-get-cities';
import { useLocale } from '@/hooks/use-locale';
import { navigateToCity } from '@/features/marketplace/utils/city-navigation';

const Leading: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
};

const Trailing: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
};

const ConnectedHeader: React.FC<ConnectedHeaderProps> & {
  Leading: React.FC<{ children: React.ReactNode }>;
  Trailing: React.FC<{ children: React.ReactNode }>;
  MobileSideMenuLeading: React.FC<{ children: React.ReactNode }>;
} = ({ children }) => {
  const { t } = useTranslation();
  const [showCitiesDropdown, setShowCitiesDropdown] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { currentLocale } = useLocale();

  const handleClickOutside = useCallback((event: MouseEvent) => {
    const target = event.target as HTMLElement;
    const isInsidePortalDropdown = target.closest('[data-cities-dropdown="true"]');

    if (buttonRef.current && !buttonRef.current.contains(target) && !isInsidePortalDropdown) {
      setShowCitiesDropdown(false);
    }
  }, []);

  useEffect(() => {
    if (!showCitiesDropdown) return;
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCitiesDropdown, handleClickOutside]);

  const filledSlots = useMemo(() => {
    const slots: Slots = {
      leading: null,
      trailing: null,
      mobileSideMenuLeading: null,
    };

    Children.toArray(children).forEach((child) => {
      if (!isValidElement(child)) {
        return;
      }

      switch (child.type) {
        case Leading:
          slots.leading = child;
          break;
        case Trailing:
          slots.trailing = child;
          break;
        case ConnectedMobileSideMenu.Leading:
          slots.mobileSideMenuLeading = child;
          break;
        default:
      }
    });

    return slots;
  }, [children]);

  const { user } = useAuthContext();

  return (
    <CitiesListContextProvider>
      <Header>
        <Header.Leading>{filledSlots.leading}</Header.Leading>
        <Header.Trailing className={user ? 'gap-5' : 'gap-4'}>
          {filledSlots.trailing}
          <div className="hidden xl:flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <div className="relative px-2 py-3 hover:rounded-lg hover:bg-bg-primaryLight/40">
                    <button
                      ref={buttonRef}
                      onClick={() => setShowCitiesDropdown(!showCitiesDropdown)}
                      aria-expanded={showCitiesDropdown}
                      aria-haspopup="listbox"
                      style={{
                        fontSize: 16,
                      }}
                      className="flex items-center gap-1 text-[16px] font-bold text-N900"
                    >
                      {t('js.features.shared.header.cities')}{' '}
                      <IconSaxIcon
                        name={showCitiesDropdown ? 'arrow-up-1' : 'arrow-down'}
                        size={18}
                        strokeColor={theme?.colors.black}
                      />
                    </button>
                    <CitiesGridModal
                      isOpen={showCitiesDropdown}
                      onClose={() => setShowCitiesDropdown(false)}
                      onCitySelect={(city) => navigateToCity(city, currentLocale)}
                    />
                  </div>
                  <div className="px-2 py-3 hover:rounded-lg hover:bg-bg-primaryLight/40">
                    <Header.HeaderLink href="https://help.colochousing.com/">
                      {t('js.features.shared.header.contact_us')}
                    </Header.HeaderLink>
                  </div>
                </div>
                <div className="flex items-center">
                  {user ? (
                    <div className="px-2 py-3 hover:rounded-lg hover:bg-bg-primaryLight/40">
                      <UserMenu />
                    </div>
                  ) : (
                    <div className="px-2 py-3 hover:rounded-lg hover:bg-bg-primaryLight/40">
                      <Header.HeaderLink href={`${COLOC_MAIN_APP}/users/sign_in`}>
                        {t('js.features.shared.header.login')}
                      </Header.HeaderLink>
                    </div>
                  )}
                  <div className="p-3 hover:rounded-lg hover:bg-bg-primaryLight/40">
                    <LocaleSwitcher />
                  </div>
                </div>
          </div>
          <div className="xl:hidden">
            <ConnectedMobileSideMenu>
              {filledSlots.mobileSideMenuLeading}
            </ConnectedMobileSideMenu>
          </div>
        </Header.Trailing>
      </Header>
    </CitiesListContextProvider>
  );
};

ConnectedHeader.Leading = Leading;
ConnectedHeader.Trailing = Trailing;
ConnectedHeader.MobileSideMenuLeading = ConnectedMobileSideMenu.Leading;

export { ConnectedHeader };
